import React, { useState } from "react";
import { heicTo } from "heic-to";
import { resizeImageToMaxSize, base64BlobLimit } from "./imageResizeUtils";
import { SvgIcon } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Upload } from "@progress/kendo-react-upload";
import type { UploadFileInfo } from "@progress/kendo-react-upload";
import type { Upload as UploadType } from "@progress/kendo-react-upload";
import { plusIcon, pencilIcon } from '@progress/kendo-svg-icons';

function isHeic(file: File): boolean {
  return file.type === "image/heic" || file.name.toLowerCase().endsWith(".heic");
}

async function toDisplayableBlob(file: File): Promise<Blob> {
  if (isHeic(file)) {
    return heicTo({ blob: file, type: "image/jpeg", quality: 0.9 });
  }
  return file;
}

type Base64Mode = "dataUrl" | "raw";

type Props = {
  /** Initial base64 from backend (can be DataURL or raw; see base64ModeIncoming) */
  value?: string | null;

  /** Called when image is selected. Second param (optional) contains image dimensions for scaling pins on image change. */
  onChange: (base64: string | null, dimensions?: { width: number; height: number }) => void;

  /** Accept file types, defaults to images */
  accept?: string; // default: "image/*"

  /** Maximum size in MB (optional) */
  maxSizeMb?: number;

  /** If true, disables interaction */
  disabled?: boolean;

  /**
   * How you want to store/send base64 to backend:
   * - "dataUrl": "data:image/png;base64,AAAA..."
   * - "raw": only "AAAA..." (without prefix)
   */
  base64ModeOutgoing?: Base64Mode; // default: "raw"

  /**
   * How the value is coming (if already from backend):
   * - "dataUrl": already comes with "data:image/...;base64,"
   * - "raw": only payload, and you also pass incomingMimeType
   */
  base64ModeIncoming?: Base64Mode; // default: "raw"
  incomingMimeType?: string; // used if base64ModeIncoming = "raw"

  /** Error callback (e.g., file too large, invalid type) */
  onError?: (message: string) => void;

  /** If true, shows pencil icon instead of plus icon (for edit mode) */
  isEditMode?: boolean;

  /** When provided, use this Upload instead of rendering one. The button will trigger the external Upload's file input. */
  uploadRef?: React.RefObject<UploadType | null>;
};

function dataUrlToRaw(dataUrl: string): string {
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

function isSvgFile(file: File): boolean {
  return file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");
}

/** Convert SVG data URL to PNG for compatibility with map/display components */
async function svgToPngDataUrl(dataUrl: string): Promise<string> {
  const dimensions = getSvgDimensionsFromDataUrl(dataUrl);
  const width = dimensions?.width ?? 800;
  const height = dimensions?.height ?? 600;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth > 0 ? img.naturalWidth : width;
      const h = img.naturalHeight > 0 ? img.naturalHeight : height;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas not supported"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      reject(new Error("Could not load SVG for conversion."));
    };
    img.src = dataUrl;
  });
}

/** Extract width/height from SVG data URL when Image element fails (e.g. SVG with external refs) */
function getSvgDimensionsFromDataUrl(dataUrl: string): { width: number; height: number } | null {
  try {
    const comma = dataUrl.indexOf(",");
    if (comma < 0) return null;
    const payload = dataUrl.slice(comma + 1);
    const isBase64 = dataUrl.includes(";base64,");
    const svgString = isBase64 ? atob(payload) : decodeURIComponent(payload);
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, "image/svg+xml");
    const svg = doc.querySelector("svg");
    if (!svg) return null;
    const w = parseFloat(svg.getAttribute("width") ?? "");
    const h = parseFloat(svg.getAttribute("height") ?? "");
    if (w > 0 && h > 0) return { width: w, height: h };
    const viewBox = svg.getAttribute("viewBox");
    if (viewBox) {
      const parts = viewBox.trim().split(/\s+|,/);
      if (parts.length >= 4) {
        const vw = parseFloat(parts[2]);
        const vh = parseFloat(parts[3]);
        if (vw > 0 && vh > 0) return { width: vw, height: vh };
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function ImageUploadButton({
  onChange,
  accept = "image/*,image/heic,image/svg+xml",
  maxSizeMb,
  disabled,
  base64ModeOutgoing = "raw",
  onError,
  isEditMode = false,
  uploadRef: externalUploadRef,
}: Props) {
  const [isBusy, setIsBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const saveUrlAsBase64 = async (files: UploadFileInfo[]) => {
    const fileInfo = files[0];
    if (!fileInfo) return { uid: "" };
    const file = fileInfo.getRawFile?.();
    if (!(file instanceof File)) return { uid: fileInfo.uid };
    const isImage = file.type.startsWith("image/") || isHeic(file);
    if (!isImage) {
      onError?.("Please select an image file.");
      return { uid: fileInfo.uid };
    }
    try {
      setIsBusy(true);
      let blob = await toDisplayableBlob(file);
      const payloadLimitBytes = (maxSizeMb ?? 5) * 1024 * 1024;
      const blobLimitBytes = base64BlobLimit(payloadLimitBytes);
      if (blob.size > blobLimitBytes) {
        blob = await resizeImageToMaxSize(blob, blobLimitBytes);
      }
      let dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Error reading file."));
        reader.onload = () => resolve(String(reader.result));
        reader.readAsDataURL(blob);
      });
      if (isSvgFile(file)) {
        dataUrl = await svgToPngDataUrl(dataUrl);
      }
      const outgoing = base64ModeOutgoing === "dataUrl" ? dataUrl : dataUrlToRaw(dataUrl);
      const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => reject(new Error("Could not load image."));
        img.src = dataUrl;
      });
      setUploadError(null);
      onChange(outgoing, dimensions);
      return { uid: fileInfo.uid };
    } catch {
      onError?.("Could not process the image.");
      return { uid: fileInfo.uid };
    } finally {
      setIsBusy(false);
    }
  };

  const extensions = [".jpg", ".jpeg", ".png", ".bmp", ".webp", ".heic", ".svg"];
  const maxFileSizeForSelection = 20 * 1024 * 1024;

  const handleAdd = () => {
    setUploadError(null);
  };

  if (externalUploadRef) {
    return (
      <Button
        type="button"
        themeColor="primary"
        svgIcon={isEditMode ? pencilIcon : plusIcon}
        disabled={disabled || isBusy}
        title={isEditMode ? "Change Image" : "Upload Image"}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const upload = externalUploadRef.current as { actionElement?: HTMLInputElement } | null;
          upload?.actionElement?.click();
        }}
        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
      />
    );
  }

  return (
    <div className="image-upload-button-only flex flex-col items-start gap-1">
      {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
      <style>{`
        .image-upload-button-only .k-upload-button {
          background-color: var(--kendo-color-primary);
          color: var(--kendo-color-primary-contrast, #fff);
          border-color: var(--kendo-color-primary);
          width: 30px !important;
          height: 30px !important;
          min-width: 30px !important;
          min-height: 30px !important;
          padding: 6px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .image-upload-button-only .k-upload-status {
          display: none !important;
        }
      `}</style>
      <Upload
        batch={false}
        multiple={false}
        defaultFiles={[]}
        showFileList={false}
        showActionButtons={false}
        autoUpload={true}
        accept={accept}
        disabled={disabled || isBusy}
        restrictions={{ allowedExtensions: extensions, maxFileSize: maxFileSizeForSelection }}
        onAdd={handleAdd}
        saveUrl={saveUrlAsBase64}
        removeUrl={async () => ({ uid: "" })}
        selectMessageUI={() => (
          <span title={isEditMode ? "Change Image" : "Upload Image"}>
            <SvgIcon icon={isEditMode ? pencilIcon : plusIcon} />
          </span>
        )}
        className="
          !inline-block !w-fit
          [&_.k-upload-dropzone]:!min-h-0 [&_.k-upload-dropzone]:!p-0 [&_.k-upload-dropzone]:!border-0
          [&_.k-upload-dropzone]:!bg-transparent [&_.k-dropzone-hint]:!hidden
        "
      />
    </div>
  );
}
